import React, { useEffect, useRef, useState } from "react";
import { Table } from "antd";
import { DatatableProps } from "../../data/interface";

const Datatable: React.FC<DatatableProps> = ({
  columns,
  dataSource,
  Selection,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [Selections, setSelections] = useState<any>(true);
  const [filteredDataSource, setFilteredDataSource] = useState(dataSource);
  const [showSettings, setShowSettings] = useState(false);

  const settingsRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const LOCAL_STORAGE_KEY = "visible_columns";

  // Load visible columns from localStorage or use all
  const getInitialVisibleColumns = () => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      const storedKeys = JSON.parse(stored);
      return columns.filter((col) =>
        storedKeys.includes(col.key || col.dataIndex)
      );
    }
    return columns;
  };

  const [visibleColumns, setVisibleColumns] = useState(
    getInitialVisibleColumns
  );

  const onSelectChange = (newSelectedRowKeys: any[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleColumnChange = (key: string) => {
    const currentKeys = visibleColumns.map((col) => col.key || col.dataIndex);
    let updatedKeys: any;

    if (currentKeys.includes(key)) {
      updatedKeys = currentKeys.filter((k) => k !== key);
    } else {
      updatedKeys = [...currentKeys, key];
    }

    const newVisibleCols = columns.filter((col) =>
      updatedKeys.includes(col.key || col.dataIndex)
    );
    setVisibleColumns(newVisibleCols);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedKeys));
  };

  useEffect(() => {
    if (Array.isArray(dataSource)) {
      setFilteredDataSource(dataSource);
    }
  }, [dataSource]);

  useEffect(() => {
    setSelections(Selection);
  }, [Selection]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowSettings(false);
      }
    };

    if (showSettings) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettings]);

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <>
      <div className="table-top-data">
        <div className="row p-3">
          <div className="col-sm-12 text-end position-relative">
            <button
              ref={buttonRef}
              className="btn btn-sm btn-outline-primary"
              onClick={(e) => {
                e.stopPropagation();
                setShowSettings(!showSettings);
              }}
            >
              ⚙️ Column Settings
            </button>

            {showSettings && (
              <div
                ref={settingsRef}
                className="position-absolute bg-light border rounded p-3 mt-2"
                style={{
                  top: "100%",
                  right: 0,
                  zIndex: 1000,
                  width: "250px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              >
                {columns.map((col) => {
                  const key = col.key || col.dataIndex;
                  return (
                    <div
                      key={key}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label htmlFor={`col-${key}`} className="form-label mb-0">
                        {col.title}
                      </label>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`col-${key}`}
                        checked={visibleColumns.some(
                          (c) => (c.key || c.dataIndex) === key
                        )}
                        onChange={() => handleColumnChange(key)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Table
        className="table datanew dataTable no-footer"
        columns={visibleColumns}
        dataSource={filteredDataSource}
        rowHoverable={false}
        rowKey="id"
        {...(Selections ? { rowSelection } : {})}
        pagination={{
          locale: { items_per_page: "" },
          nextIcon: <i className="ti ti-chevron-right" />,
          prevIcon: <i className="ti ti-chevron-left" />,
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30"],
          showTotal: (total, range) =>
            `Showing ${range[0]} - ${range[1]} of ${total} entries`,
        }}
      />
    </>
  );
};

export default Datatable;
